import { useState, useEffect } from "react";
import "./HotSpots.scss";
import { IsMobileContent } from "../../../media-queries/MediaQueries";
import { useHotSpotProduct } from "./useHotSpotProduct";
import {
  ProductHotSpotContent,
  GenericHotSpotContent,
  HotSpotLoadingContent,
} from "./HotSpotContent";

const HotSpots = ({
  hotSpots,
  productUrl,
  productAPIErrorText,
  addYourBrandLabel,
  newProductLabel,
  parentComponentId,
}) => {
  // UI state
  const [activeHotSpot, setActiveHotSpot] = useState(-1);
  const [isHover, setHover] = useState(false);
  const [isTileHover, setTileHover] = useState(false);
  const [isThumbClicked, setIsVideoThumbClicked] = useState(false);

  // Product data management via custom hook
  const productHotSpot = useHotSpotProduct();

  const isMobile = IsMobileContent();

  /**
   * Determine if a hot spot is a product or generic content
   */
  const isProductHotSpot = (item) => {
    return (
      item.productTileInfo.productcode &&
      item.productTileInfo.productcode !== "" &&
      item.productData !== undefined
    );
  };

  /**
   * Handle hot spot interaction (click/hover)
   */
  const handleHotSpotActivate = (item, index) => {
    productHotSpot.clearProduct();
    setIsVideoThumbClicked(false);

    const isProduct = isProductHotSpot(item);

    if (isProduct) {
      productHotSpot.loadProduct(item.productData);
    }

    if (activeHotSpot === index) {
      setActiveHotSpot(-1);
    } else {
      setActiveHotSpot(index);
    }
  };

  /**
   * Close active hot spot
   */
  const handleClose = () => {
    productHotSpot.clearProduct();
    setActiveHotSpot(-1);
    setIsVideoThumbClicked(false);
  };

  /**
   * Calculate position for hot spot content tile
   */
  const buildStyleAttributeObject = (x, y, unit = "%") => {
    const obj = {};

    if (isMobile) {
      obj["right"] = 0;
      obj["left"] = 0;
      obj["bottom"] = 0;
      obj["top"] = 0;
      return obj;
    }

    if (x >= 50) {
      obj["right"] = 100 - x + unit;
    } else {
      obj["left"] = x + unit;
    }

    if (y >= 50) {
      obj["bottom"] = 100 - y + unit;
    } else {
      obj["top"] = Math.max(Math.min(50 + (y - 50), 95), 5) + unit;
    }

    return obj;
  };

  /**
   * Focus on hot spot content for accessibility
   */
  const focusOnContent = () => {
    const element = document.getElementById("hot-spot-content");
    if (element) element.focus();
  };

  /**
   * Handle escape key to close hot spot
   */
  const handleEscapeKey = (e) => {
    const oldActive = activeHotSpot;
    if (e.keyCode === 27) {
      setTileHover(false);
      handleClose();
      const button = document.getElementById(`hotspot-button-${oldActive}-${parentComponentId}`);
      if (button) button.focus();
    }
  };

  /**
   * Render all hot spot markers
   */
  const renderHotSpots = () => {
    return hotSpots.map((item, index) => {
      const { x, y, hasInvalidProduct } = item;
      const unit = "%";

      if (hasInvalidProduct) return null;

      return (
        <div
          key={`hotspot-container-${index}`}
          className="__spot"
          style={{
            left: x + unit,
            top: Math.max(Math.min(50 + (y - 50), 95), 5) + unit,
          }}
          onClick={() => handleHotSpotActivate(item, index)}
          onMouseLeave={() => {
            if (isMobile) return;
            setHover(false);
          }}
          onMouseOut={() => {
            if (isMobile) return;
            setHover(false);
          }}
          onMouseMove={() => {
            if (isMobile) return;
            setHover(true);
          }}
          onMouseOver={() => {
            if (isMobile) return;
            setHover(true);
          }}
          onFocus={() => {
            setHover(true);
          }}
          onMouseEnter={() => {
            if (isMobile) return;
            if (activeHotSpot !== index) {
              handleHotSpotActivate(item, index);
            }
          }}
        >
          <button
            type="button"
            id={`hotspot-button-${index}-${parentComponentId}`}
            className={activeHotSpot === index ? "__item __item--active" : "__item"}
            tabIndex="0"
            aria-pressed={activeHotSpot === index}
          >
            <div className="__layer __layer--1" tabIndex="-1"></div>
            <div className="__layer __layer--2" tabIndex="-1"></div>
            <div className="__layer __layer--3" tabIndex="-1"></div>
          </button>
        </div>
      );
    });
  };

  /**
   * Render the active hot spot content tile
   */
  const renderHotSpotContent = () => {
    if (activeHotSpot === -1) {
      return null;
    }

    const item = hotSpots[activeHotSpot];
    
    // Defensive check for missing item data
    if (!item || !item.productTileInfo) {
      console.error('HotSpots: Invalid hot spot data at index', activeHotSpot);
      return null;
    }
    
    const { x, y, productTileInfo } = item;
    const style = buildStyleAttributeObject(x, y);
    const isProduct = isProductHotSpot(item);

    // Show loading state
    if (productHotSpot.loading) {
      return (
        <HotSpotLoadingContent
          style={style}
          onMouseEnter={() => setTileHover(true)}
          onMouseLeave={() => setTileHover(false)}
        />
      );
    }

    // Render product hot spot
    if (isProduct) {
      return (
        <ProductHotSpotContent
          style={style}
          productData={productHotSpot.productData}
          productTileInfo={productTileInfo}
          addYourBrandLabel={addYourBrandLabel}
          newProductLabel={newProductLabel}
          onClose={handleClose}
          onMouseEnter={() => setTileHover(true)}
          onMouseLeave={() => setTileHover(false)}
          onKeyUp={handleEscapeKey}
        />
      );
    }

    // Render generic content hot spot
    return (
      <GenericHotSpotContent
        style={style}
        tileInfo={productTileInfo}
        addYourBrandLabel={addYourBrandLabel}
        newProductLabel={newProductLabel}
        onClose={handleClose}
        onMouseEnter={() => setTileHover(true)}
        onMouseLeave={() => setTileHover(false)}
        onKeyUp={handleEscapeKey}
        isVideoClicked={isThumbClicked}
        onVideoClick={() => setIsVideoThumbClicked(!isThumbClicked)}
      />
    );
  };

  // Focus on content after render
  useEffect(() => {
    if (activeHotSpot !== -1) {
      const timer = setTimeout(() => {
        focusOnContent();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [activeHotSpot]);

  if(!Array.isArray(hotSpots)) {
      return null;
  }

  return (
    <div 
      id="hot-spots" 
      className="hot-spots" 
      key="hot-spots"
      onMouseMove={ () => {
        if(!isHover && !isTileHover){
          handleClose();
        }
      }}>
      {renderHotSpots()}
      {renderHotSpotContent()}
    </div>
  );
}

export default HotSpots;